import React from "react";

const channels = [
  "Behavioural Science",
  "AI news & views",
  "Creativity & Leadership",
  "Storytelling",
  "Brand & Strategy",
];

const perks = [
  {
    icon: "icon-hub-chat",
    title: "Talk with clever people",
    description:
      "No algorithm, no ads, no influencers. Just practitioners thinking out loud.",
  },
  {
    icon: "icon-hub-sparkle",
    title: "AI tools that earn their keep",
    description:
      "Briefing tool, behavioural science solver, more we keep building. All inside the Hub.",
  },
  {
    icon: "icon-hub-books",
    title: "Handy file library",
    description:
      "Trend reports, decks and hard-to-find research. Yours to save, share, and use the bits you need.",
  },
  {
    icon: "icon-hub-cuppa",
    title: "Office hours with the team",
    description:
      "Stuck on a brief or a course? Book 15 minutes with us when you need a hand.",
  },
];

interface Props {
  handleShowSignInSideBar: () => void;
}

const CommunityHub: React.FC<Props> = ({ handleShowSignInSideBar }) => (
  <>
    <section className="hub-section" aria-labelledby="hub-title">
      <div className="container">
        <div className="header">
          <p className="eyebrow">Plus the Community Hub</p>
          <h2 className="section-title" id="hub-title">
            Where clever people are unusually generous with what they know.
          </h2>
          <p className="section-sub">
            A private space for creative thinkers, behavioural science fans,
            marketers, and entrepreneurs in 170+ countries. Access included with
            every plan.
          </p>
        </div>

        <div
          className="hub-preview"
          aria-label="A look inside the Community Hub"
        >
          <div className="hub-preview-chrome" aria-hidden="true">
            <span className="hub-preview-dot dot-red"></span>
            <span className="hub-preview-dot dot-amber"></span>
            <span className="hub-preview-dot dot-green"></span>
            <span className="hub-preview-url">42courses.com/community</span>
          </div>
          <div className="hub-preview-body">
            <p className="hub-preview-label">
              <span className="pulse-dot" aria-hidden="true"></span>
              Top topics
            </p>
            <ul className="hub-channels" role="list">
              {channels.map((channel) => (
                <li className="hub-channel" key={channel}>
                  {channel}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <ul className="hub-perks" role="list">
          {perks.map((perk) => (
            <li className="hub-perk" key={perk.title}>
              <span className="hub-perk-icon" aria-hidden="true">
                <svg viewBox="0 0 48 48">
                  <use href={`#${perk.icon}`} />
                </svg>
              </span>
              <h3>{perk.title}</h3>
              <p>{perk.description}</p>
            </li>
          ))}
        </ul>

        <div className="hub-cta">
          <div className="hub-stat-strip" aria-label="Community statistics">
            <span>
              <strong>1,000+</strong> members
            </span>
            <span>
              <strong>170+</strong> countries
            </span>
            <span>
              <strong>4.9&#9733;</strong> average rating
            </span>
          </div>
          <p className="hub-signin-line">
            Already a member?{" "}
            <button type="button" onClick={handleShowSignInSideBar}>
              Sign in &rarr;
            </button>
          </p>
        </div>
      </div>
    </section>

    <svg style={{ display: "none" }} aria-hidden="true">
      <symbol id="icon-hub-chat" viewBox="0 0 48 48">
        <path
          d="M9 12 h26 a4 4 0 0 1 4 4 v14 a4 4 0 0 1 -4 4 h-14 l-8 6 v-6 h-4 a4 4 0 0 1 -4 -4 v-14 a4 4 0 0 1 4 -4 z"
          fill="#F5B041"
          fillOpacity=".15"
          stroke="#C4841D"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <circle cx="17" cy="23" r="2" fill="#C4841D" />
        <circle cx="24" cy="23" r="2" fill="#C4841D" />
        <circle cx="31" cy="23" r="2" fill="#C4841D" />
      </symbol>
      <symbol id="icon-hub-sparkle" viewBox="0 0 48 48">
        <path
          d="M24 6 L26.7 18.3 L39 21 L26.7 23.7 L24 36 L21.3 23.7 L9 21 L21.3 18.3 Z"
          fill="#F5B041"
          fillOpacity=".22"
          stroke="#C4841D"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <path
          d="M37 33 L38 37 L42 38 L38 39 L37 43 L36 39 L32 38 L36 37 Z"
          fill="#C4841D"
        />
        <path
          d="M11 8 L11.7 10.3 L14 11 L11.7 11.7 L11 14 L10.3 11.7 L8 11 L10.3 10.3 Z"
          fill="#C4841D"
        />
      </symbol>
      <symbol id="icon-hub-books" viewBox="0 0 48 48">
        <rect
          x="7"
          y="13"
          width="9"
          height="28"
          rx="1.5"
          fill="#F5B041"
          fillOpacity=".22"
          stroke="#C4841D"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <line x1="7" y1="20" x2="16" y2="20" stroke="#C4841D" strokeWidth="2" />
        <rect
          x="19"
          y="9"
          width="9"
          height="32"
          rx="1.5"
          fill="#F5B041"
          fillOpacity=".18"
          stroke="#C4841D"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <line
          x1="19"
          y1="16"
          x2="28"
          y2="16"
          stroke="#C4841D"
          strokeWidth="2"
        />
        <line
          x1="19"
          y1="34"
          x2="28"
          y2="34"
          stroke="#C4841D"
          strokeWidth="2"
        />
        <path
          d="M32 15 L41 17 L37 40 L28 38 Z"
          fill="#F5B041"
          fillOpacity=".22"
          stroke="#C4841D"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <line
          x1="30.5"
          y1="22"
          x2="38.5"
          y2="23.8"
          stroke="#C4841D"
          strokeWidth="1.6"
        />
      </symbol>
      <symbol id="icon-hub-cuppa" viewBox="0 0 48 48">
        <path
          d="M18 7 c -1.6 2.2 1.6 3 0 5.5 c -1.6 2 1.6 3 0 5"
          fill="none"
          stroke="#C4841D"
          strokeWidth="2"
          strokeLinecap="round"
          opacity=".55"
        />
        <path
          d="M24 5 c -1.6 2.2 1.6 3 0 5.5 c -1.6 2 1.6 3 0 5"
          fill="none"
          stroke="#C4841D"
          strokeWidth="2"
          strokeLinecap="round"
          opacity=".75"
        />
        <path
          d="M30 7 c -1.6 2.2 1.6 3 0 5.5 c -1.6 2 1.6 3 0 5"
          fill="none"
          stroke="#C4841D"
          strokeWidth="2"
          strokeLinecap="round"
          opacity=".55"
        />
        <path
          d="M9 22 h26 v9 a8 8 0 0 1 -8 8 h-10 a8 8 0 0 1 -8 -8 z"
          fill="#F5B041"
          fillOpacity=".22"
          stroke="#C4841D"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <path
          d="M35 25 a 5 5 0 0 1 0 8"
          fill="none"
          stroke="#C4841D"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <ellipse
          cx="22"
          cy="42"
          rx="16"
          ry="2.4"
          fill="#F5B041"
          fillOpacity=".15"
          stroke="#C4841D"
          strokeWidth="2"
        />
      </symbol>
    </svg>
  </>
);

export default CommunityHub;
